import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { fbAuth, googleProvider, fbError } from "../lib/firebase";
import api, { apiError } from "../lib/api";
import { oneClickDemo } from "../components/Layout";
import { track } from "../lib/analytics";
import { toast } from "sonner";

export function GoogleButton({ next = "/projects" }) {
  const { exchangeFirebase } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const login = async () => {
    setBusy(true);
    try {
      const cred = await signInWithPopup(fbAuth, googleProvider);
      const data = await exchangeFirebase(cred.user);
      if (data.role === "admin") navigate("/admin");
      else if (!data.company) {
        track("signup_completed", { method: "google" });
        navigate(`/complete-profile?next=${encodeURIComponent(next)}`);
      }
      else navigate(next);
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user" && e.code !== "auth/cancelled-popup-request") {
        toast.error(fbError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" onClick={login} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 border border-[#0B1F3A]/25 rounded-md py-2.5 text-sm font-medium text-[#0B1F3A] hover:bg-[#0B1F3A]/5 disabled:opacity-60" data-testid="google-login-btn">
      <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.97 10.97 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
      {busy ? "Signing in…" : "Continue with Google"}
    </button>
  );
}

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser, exchangeFirebase } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/projects";

  const legacyLogin = async () => {
    const { data } = await api.post("/auth/login", form);
    setUser(data);
    navigate(data.role === "admin" ? "/admin" : next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const cred = await signInWithEmailAndPassword(fbAuth, form.email.trim(), form.password);
      const data = await exchangeFirebase(cred.user);
      navigate(data.role === "admin" ? "/admin" : next);
    } catch (err) {
      if (["auth/user-not-found", "auth/invalid-credential", "auth/wrong-password", "auth/invalid-email"].includes(err.code)) {
        // Seeded accounts (demo/admin) live outside Firebase — fall back to the legacy login
        try {
          await legacyLogin();
        } catch (legacyErr) {
          setError(apiError(legacyErr));
        }
      } else {
        setError(fbError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const demo = async () => {
    try {
      await oneClickDemo(setUser, navigate);
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <h1 className="font-serif text-3xl text-[#0B1F3A]">Welcome back</h1>
      <p className="text-sm text-[#475569] mt-1">Log in to open your saved projects and roadmaps.</p>

      <button onClick={demo} className="mt-6 w-full bg-[#059669] text-white rounded-md py-3 font-bold text-sm hover:bg-[#059669]/90" data-testid="login-demo-btn">
        Explore the live demo → (no typing needed)
      </button>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-[#0B1F3A]/15" /><span className="text-xs text-[#475569]">or log in</span><div className="flex-1 h-px bg-[#0B1F3A]/15" />
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-sm font-medium text-[#0B1F3A]">Work email</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-1 w-full bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="login-email-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-[#0B1F3A]">Password</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-1 w-full bg-white border border-[#0B1F3A]/20 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#059669] focus:outline-none" data-testid="login-password-input" />
        </div>
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} className="w-full bg-[#1D4ED8] text-white rounded-md py-3 font-medium text-sm hover:bg-[#1638A8] disabled:opacity-60" data-testid="login-submit-btn">
          {busy ? "Logging in…" : "Log in"}
        </button>
      </form>
      <div className="mt-4"><GoogleButton next={next} /></div>
      <p className="text-sm text-[#475569] mt-6 text-center">
        New to RegNav? <Link to={`/signup?next=${encodeURIComponent(next)}`} className="text-[#0B1F3A] font-medium underline" data-testid="login-signup-link">Create a free account</Link>
      </p>
    </div>
  );
}
